import logoAllariaMas from "../assets/images/logos/allariamas.svg";
import logoAllaria from "../assets/images/logos/allaria.svg";
import logoAgroFinanzas from "../assets/images/logos/agrofinanzas.svg";
import logoAllariaFondos from "../assets/images/logos/allariafondos.svg";
import logoAllariaSecurities from "../assets/images/logos/allaria-securities.svg";
import faviconAllariaMas from "../assets/favicon.png";
import faviconAllaria from "../assets/faviconAllaria.png";
import { FormikValues } from "formik";
import { CompanyData, CompanyType, OnboardingType } from "../types";
import {
  getOnboardingReferrer,
  isIntervalores,
  isLandToken,
  isUnifiedOnboarding,
} from "../utils";
import torosCapitalLogo from "../assets/images/logos/toros-capital.png";
import mabraLogo from "../assets/images/logos/Mabra.svg";
import laBragadenseLogo from "../assets/images/logos/labragadense.svg";
import landTokenLogo from "../assets/images/logos/landtoken.svg";
import winaLogo from "../assets/images/logos/wina.svg";
import savLogo from "../assets/images/logos/sav3.png";
import bancoDelSolLogo from "../assets/images/logos/banco-del-sol.svg";
import unicredFondosLogo from "../assets/images/logos/unicred-fondos.png";
import intervaloresLogo from "../assets/images/logos/intervalores.svg";
import mudafyLogo from "../assets/images/logos/mudafy.svg";
import { store } from "../app/store";

const defaultTemplates: OnboardingType[] = [
  "PHYSICAL",
  "JURIDICAL",
  "SIGNATORY",
];

export const getCompanyData = (
  formValues?: FormikValues | null,
): CompanyData => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  const staticUrl = import.meta.env.VITE_BASE_URL_STATIC;

  switch (company) {
    case "ALLARIA":
      return {
        formTitle: "Onboarding Allaria",
        logo: logoAllaria,
        logoKey: "allaria",
        favicon: faviconAllaria,
        companyName: company,
        phone: "5491128608888",
        mail: "atencionalcliente@allaria.com.ar",
        partnerships: [
          {
            key: "landtoken",
            name: "Landtoken",
            logo: landTokenLogo,
            logoClassname: "landToken",
            onboardingTypes: ["PHYSICAL"],
          },
          {
            key: "toroscapital",
            name: "Toros Capital",
            logo: torosCapitalLogo,
            logoClassname: "torosLogo",
            onboardingTypes: defaultTemplates,
          },
          {
            key: "mabra",
            name: "MABRA",
            logo: mabraLogo,
            logoClassname: "mabra",
            onboardingTypes: defaultTemplates,
          },
          {
            key: "wina",
            name: "Wina",
            logo: winaLogo,
            logoClassname: "winaLogo",
            onboardingTypes: defaultTemplates,
          },
          {
            key: "bancodelsol",
            name: "Banco del Sol",
            logo: bancoDelSolLogo,
            logoClassname: "bancoDelSolLogo",
            onboardingTypes: ["PHYSICAL"],
            // The consent flow's own redirect lives in the partner registry in
            // `utils/partnerConsent`, not here: it only applies to links that
            // arrive from the partner's app, and keeping it there is what lets
            // that flow work whichever company the app is built as.
          },
        ],
        termsAndConditions: [
          {
            name: "termsAndConditions",
            required: true,
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
                {" y "}
                <span id="platform-use">Uso de la plataforma</span>.
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones de Allaria",
                url: `${staticUrl}/terms/T%26C+Allaria+con+A%2B.pdf`,
              },
              {
                id: "platform-use",
                name: "Uso de la plataforma",
                url: `${staticUrl}/terms/Términos+y+condiciones+de+Uso+de+la+Plataforma.pdf`,
              },
            ],
          },
          ...(formValues && isLandToken(formValues || {})
            ? [
                // Uncomment in V2,
                // {
                //   required: true,
                //   name: "landTokenTermsAndConditions",
                //   label: (
                //     <div>
                //       Acepto los{" "}
                //       <span id="landtoken-terms">Términos y Condiciones</span>{" "}
                //       de Landtoken
                //     </div>
                //   ),
                //   links: [
                //     {
                //       id: "landtoken-terms",
                //       name: "Términos y Condiciones de Landtoken",
                //       url: `${staticUrl}/terms/Nota+SENEBI.pdf`,
                //     },
                //   ],
                // },
              ]
            : []),
          {
            required: false,
            name: "senebiAndExteriorNotes",
            label: (
              <div>
                Acepto la <span id="senebi-note">Nota SENEBI</span> y{" "}
                <span id="exterior-note">Nota exterior</span>.
              </div>
            ),
            links: [
              {
                id: "senebi-note",
                name: "Nota SENEBI",
                url: `${staticUrl}/terms/Nota+SENEBI.pdf`,
              },
              {
                id: "exterior-note",
                name: "Nota exterior",
                url: `${staticUrl}/terms/Nota+de+operaciones+en+el+exterior.pdf`,
              },
            ],
          },
        ],
      };
    case "AGROFINANZAS":
      return {
        formTitle: "Onboarding Agro Finanzas",
        logo: logoAgroFinanzas,
        logoClassname: "agroFinanzasLogo",
        logoKey: "agro",
        favicon: faviconAllariaMas,
        companyName: company,
        phone: "541167678324",
        mail: "compliance@allariaagro.com.ar",
        partnerships: [
          {
            key: "mabra",
            name: "MABRA",
            logo: mabraLogo,
            logoClassname: "mabra",
            onboardingTypes: defaultTemplates,
          },
        ],
        termsAndConditions: [
          {
            required: true,
            name: "termsAndConditions",
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>.
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones Agrofinanzas",
                url: `${staticUrl}/terms/Términos+y+Condiciones+Agrofinanzas.pdf`,
              },
            ],
          },
          {
            required: false,
            name: "wantsAllariaAccount",
            label: (
              <div>
                Quiero una comitente Allaria. Ver{" "}
                <span id="allaria-account">Términos y Condiciones</span>.
              </div>
            ),
            links: [
              {
                id: "allaria-account",
                name: "Términos y Condiciones de Allaria",
                url: `${staticUrl}/terms/T%26C+Allaria+con+A%2B.pdf`,
              },
            ],
          },
          {
            required: false,
            name: "senebiAndExteriorNotes",
            label: (
              <div>
                Acepto la <span id="senebi-note">Nota SENEBI</span> y{" "}
                <span id="exterior-note">Nota exterior</span>.
              </div>
            ),
            links: [
              {
                id: "senebi-note",
                name: "Nota SENEBI",
                url: `${staticUrl}/terms/11.Instrucción+op+SENEBI+-Mayo+23.pdf`,
              },
              {
                id: "exterior-note",
                name: "Nota exterior",
                url: `${staticUrl}/terms/10.Instrucción+op+Exterior+-Mayo+23.pdf`,
              },
            ],
          },
        ],
      };
    case "ALLARIAFONDOS":
      return {
        formTitle: "Onboarding Allaria Fondos",
        logo: logoAllariaFondos,
        logoClassname: "agroFinanzasLogo",
        logoKey: "fondos",
        favicon: faviconAllaria,
        companyName: company,
        phone: "5491128608888",
        mail:
          formValues && isIntervalores(formValues)
            ? "info@intervaloresnet.com.ar"
            : "legales@allariafondos.com.ar",
        termsAndConditions: [
          {
            required: true,
            name: "termsAndConditions",
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>.
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                ...(formValues && isIntervalores(formValues)
                  ? {
                      name: "Terminos y Condiciones FAL",
                      url: `${staticUrl}/terms/Terminos%20y%20Condiciones%20FAL.pdf`,
                    }
                  : {
                      name: "Términos y Condiciones Allaria Fondos",
                      url: `${staticUrl}/terms/Términos+y+Condiciones+de+Allaria.pdf`,
                    }),
              },
            ],
          },
          // Intervalores companies only sign the FAL terms.
          ...(formValues && isIntervalores(formValues)
            ? []
            : [
                {
                  required: false,
                  name: "acceptedFalTermsAndConditions",
                  label: (
                    <div>
                      Acepto los{" "}
                      <span id="fal-terms-and-conditions">
                        Términos y Condiciones de FAL
                      </span>
                      .
                    </div>
                  ),
                  links: [
                    {
                      id: "fal-terms-and-conditions",
                      name: "Terminos y Condiciones FAL",
                      url: `${staticUrl}/terms/Terminos%20y%20Condiciones%20FAL.pdf`,
                    },
                  ],
                },
                {
                  required: false,
                  name: "wantsAllariaAccount",
                  label: (
                    <div>
                      Acepto abrir una{" "}
                      <span id="allaria-account">cuenta en Allaria</span> para
                      ampliar mis oportunidades de inversión, y el{" "}
                      <span id="allaria-platform-terms">
                        Uso de plataforma de Allaria
                      </span>
                      .
                    </div>
                  ),
                  links: [
                    {
                      id: "allaria-platform-terms",
                      name: "Uso de plataforma de Allaria",
                      url: `${staticUrl}/terms/Términos+y+condiciones+de+Uso+de+la+Plataforma.pdf`,
                    },
                    {
                      id: "allaria-account",
                      name: "cuenta en Allaria",
                      url: `${staticUrl}/terms/T%26C+Allaria+con+A%2B.pdf`,
                    },
                  ],
                },
              ]),
        ],
        partnerships: [
          {
            key: "toroscapital",
            name: "Toros Capital",
            logo: torosCapitalLogo,
            logoClassname: "torosLogo",
            onboardingTypes: ["JURIDICAL", "SIGNATORY"],
          },
          {
            key: "unicred",
            name: "unicred Fondos",
            logo: unicredFondosLogo,
            logoClassname: "unicredLogo",
            onboardingTypes: ["JURIDICAL", "SIGNATORY"],
          },
          {
            key: "intervalores",
            name: "Intervalores",
            logo: intervaloresLogo,
            logoClassname: "intervaloresLogo",
            onboardingTypes: ["JURIDICAL", "SIGNATORY"],
            skipActiveAccountSummary: true,
          },
        ],
      };
    case "ALLARIA_SECURITY":
      return {
        formTitle: "Onboarding Allaria Securities LLC",
        logo: logoAllariaSecurities,
        logoClassname: "securitiesLogo",
        favicon: faviconAllaria,
        companyName: company,
        termsAndConditions: [
          {
            name: "termsAndConditions",
            required: true,
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones de Allaria",
                url: `${staticUrl}/terms/Allaria%20Términos%20y%20Condiciones.pdf`,
              },
            ],
          },
          {
            required: false,
            name: "senebiAndExteriorNotes",
            label: (
              <div>
                Acepto la <span id="senebi-note">Nota SENEBI</span> y{" "}
                <span id="exterior-note">Nota exterior</span>
              </div>
            ),
            links: [
              {
                id: "senebi-note",
                name: "Nota SENEBI",
                url: `${staticUrl}/terms/Nota+SENEBI.pdf`,
              },
              {
                id: "exterior-note",
                name: "Nota exterior",
                url: `${staticUrl}/terms/Nota+de+operaciones+en+el+exterior.pdf`,
              },
            ],
          },
        ],
        phone: "5491140411111",
        mail: "atencionalcliente@allaria.com.ar",
      };
    case "ALLARIAMAS":
    default:
      return {
        formTitle: "Onboarding Allaria+",
        logo: logoAllariaMas,
        logoKey: "allaria-mas",
        favicon: faviconAllariaMas,
        companyName: company,
        phone: "5491138499171",
        mail: "soporte@allariamas.com.ar",
        partnerships: [
          {
            key: "labragadense",
            name: "labragadense",
            logo: laBragadenseLogo,
            logoClassname: "laBragadense",
            onboardingTypes: defaultTemplates,
          },
          {
            key: "mabra",
            name: "MABRA",
            logo: mabraLogo,
            logoClassname: "mabra",
            onboardingTypes: defaultTemplates,
          },
          {
            key: "sav3",
            name: "SAV3",
            logo: savLogo,
            logoClassname: "savLogo",
            onboardingTypes: ["PHYSICAL"],
            replacesCompanyLogo: true,
            theme: "SAV3",
            successRedirect: {
              buttonText: "Ir a SAV3",
              message: (s) =>
                `En ${s} segundo${s !== 1 ? "s" : ""} te vamos a redirigir a SAV3`,
              autoRedirectMs: 5000,
              allowedDomains: ["sav3.io"],
            },
            merchants: [
              {
                key: "mudafy",
                logo: mudafyLogo,
              },
            ],
          },
        ],
        termsAndConditions: [
          {
            required: true,
            name: "termsAndConditions",
            label: (
              <div>
                Acepto los{" "}
                <span id="terms-and-conditions">Términos y Condiciones</span>
                {" y "}
                {isUnifiedOnboarding() ? (
                  <>la unificación entre cuentas</>
                ) : (
                  <span id="platform-use">Uso de la plataforma</span>
                )}
                .
              </div>
            ),
            links: [
              {
                id: "terms-and-conditions",
                name: "Términos y Condiciones de Allaria+",
                url: `${staticUrl}/terms/2)+TyC+Allaria%2B+Cuenta+%2B+Anexo+I+sin+aclaracion+(web+y+OB+Allaria%2B).pdf`,
              },
              {
                id: "platform-use",
                name: "Uso de la plataforma",
                url: `${staticUrl}/terms/Términos+y+condiciones+de+Uso+de+la+Plataforma.pdf`,
              },
            ],
          },
        ],
      };
    case "ALLARIADIGITAL":
      return {
        formTitle: "Allaria Digital",
        logo: logoAllaria,
        logoKey: "digital",
        favicon: faviconAllaria,
        companyName: company,
        phone: "5491128608888",
        mail: "atencionalcliente@allaria.com.ar",
        termsAndConditions: [],
      };
  }
};

export const getPartnership = (formValues?: FormikValues | null) => {
  const { partnerships } = getCompanyData(formValues);

  const {
    activeTemplate: { onboardingType },
  } = store.getState();

  const referrer = getOnboardingReferrer(formValues)?.toLowerCase() ?? null;

  const decodedSearch = decodeURIComponent(
    document.location.search,
  ).toLowerCase();

  return partnerships?.find(
    ({ key, onboardingTypes }) =>
      (referrer ? referrer.includes(key) : decodedSearch.includes(key)) &&
      (!onboardingType || onboardingTypes?.includes(onboardingType)),
  );
};

//Without a referrer there is no partnership: unlike getPartnership, it never
//falls back to the URL
export const getReferrerPartnershipKey = (referrer?: string | null) =>
  referrer ? getPartnership({ referrer })?.key : undefined;

export const getMerchantKey = (formValues?: FormikValues | null): string => {
  const fromForm = formValues?.merchant as string | undefined;
  if (fromForm) return fromForm;

  const params = new URLSearchParams(document.location.search);
  const fromRef = (params.get("ref") ?? "")
    .replace(/ /g, "+")
    .split("+")
    .filter(Boolean)[1];
  if (fromRef) return fromRef;

  return params.get("merchant") ?? "";
};

export const getMerchant = (formValues?: FormikValues | null) => {
  const partnership = getPartnership(formValues);
  if (!partnership?.merchants?.length) return undefined;

  const merchant = getMerchantKey(formValues);
  if (!merchant) return undefined;

  return partnership.merchants.find(
    (m) => m.key.toLowerCase() === merchant.toLowerCase(),
  );
};
